import { useCallback, useState, useEffect } from "react";

import { type LngLatBoundsLike, useMap } from "react-map-gl/maplibre";

import { DEFAULT_CENTER, DEFAULT_ZOOM, FLY_TO_DURATION } from "../constants";
import { type FitBoundsOptions, type FitFeaturesOptions, type FlyToOptions, type MapControlsReturn, type PanOptions, type ZoomOptions } from "../types";
import { calculateBoundsFromFeatures } from "../utils/calculate-bounds";

export function useMapControls(): MapControlsReturn {
  const mapRef = useMap();
  const [zoom, setZoomState] = useState<number | null>(null);
  const [initialZoom, setInitialZoom] = useState<number | null>(null);

  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map) return;

    const updateZoom = () => {
      const currentZoom = map.getZoom();
      setZoomState(currentZoom);
      
      if (initialZoom === null) {
        setInitialZoom(currentZoom);
      }
    };

    updateZoom();

    map.on("zoom", updateZoom);
    map.on("moveend", updateZoom);
    return () => {
      map.off("zoom", updateZoom);
      map.off("moveend", updateZoom);
    };
  }, [mapRef, initialZoom]);

  const zoomIn = useCallback(
    (options?: ZoomOptions) => {
      const map = mapRef.current?.getMap();
      if (!map) return;
      map.zoomIn({ duration: options?.duration ?? 300 });
    },
    [mapRef],
  );

  const zoomOut = useCallback(
    (options?: ZoomOptions) => {
      const map = mapRef.current?.getMap();
      if (!map) return;
      map.zoomOut({ duration: options?.duration ?? 300 });
    },
    [mapRef],
  );

  const setZoom = useCallback(
    (zoom: number, options?: ZoomOptions) => {
      const map = mapRef.current?.getMap();
      if (!map) return;
      if (options?.duration) {
        map.zoomTo(zoom, { duration: options.duration });
      } else {
        map.setZoom(zoom);
      }
    },
    [mapRef],
  );

  const panUp = useCallback(
    (distance = 0.1, options?: PanOptions) => {
      const map = mapRef.current?.getMap();
      if (!map) return;
      const currentCenter = map.getCenter();
      const zoom = map.getZoom();
      const newCenter: [number, number] = [currentCenter.lng, currentCenter.lat + distance];

      if (options?.duration) {
        map.flyTo({
          center: newCenter,
          zoom,
          duration: options.duration,
        });
      } else {
        map.panTo(newCenter, { duration: 300 });
      }
    },
    [mapRef],
  );

  const panDown = useCallback(
    (distance = 0.1, options?: PanOptions) => {
      const map = mapRef.current?.getMap();
      if (!map) return;
      const currentCenter = map.getCenter();
      const zoom = map.getZoom();
      const newCenter: [number, number] = [currentCenter.lng, currentCenter.lat - distance];

      if (options?.duration) {
        map.flyTo({
          center: newCenter,
          zoom,
          duration: options.duration,
        });
      } else {
        map.panTo(newCenter, { duration: 300 });
      }
    },
    [mapRef],
  );

  const panLeft = useCallback(
    (distance = 0.1, options?: PanOptions) => {
      const map = mapRef.current?.getMap();
      if (!map) return;
      const currentCenter = map.getCenter();
      const zoom = map.getZoom();
      const newCenter: [number, number] = [currentCenter.lng - distance, currentCenter.lat];

      if (options?.duration) {
        map.flyTo({
          center: newCenter,
          zoom,
          duration: options.duration,
        });
      } else {
        map.panTo(newCenter, { duration: 300 });
      }
    },
    [mapRef],
  );

  const panRight = useCallback(
    (distance = 0.1, options?: PanOptions) => {
      const map = mapRef.current?.getMap();
      if (!map) return;
      const currentCenter = map.getCenter();
      const zoom = map.getZoom();
      const newCenter: [number, number] = [currentCenter.lng + distance, currentCenter.lat];

      if (options?.duration) {
        map.flyTo({
          center: newCenter,
          zoom,
          duration: options.duration,
        });
      } else {
        map.panTo(newCenter, { duration: 300 });
      }
    },
    [mapRef],
  );

  const panTo = useCallback(
    (center: [number, number], options?: PanOptions) => {
      const map = mapRef.current?.getMap();
      if (!map) return;
      if (options?.duration) {
        map.flyTo({
          center,
          zoom: map.getZoom(),
          duration: options.duration,
        });
      } else {
        map.panTo(center, { duration: 300 });
      }
    },
    [mapRef],
  );

  const flyTo = useCallback(
    (options: FlyToOptions) => {
      const map = mapRef.current?.getMap();
      if (!map) return;
      map.flyTo({
        center: options.center,
        zoom: options.zoom,
        bearing: options.bearing,
        pitch: options.pitch,
        duration: options.duration ?? FLY_TO_DURATION,
      });
    },
    [mapRef],
  );

  const reset = useCallback(
    (center?: [number, number], zoom?: number) => {
      const map = mapRef.current?.getMap();
      if (!map) return;
      const resetCenter = center ?? DEFAULT_CENTER;
      const resetZoom = zoom ?? DEFAULT_ZOOM;

      map.flyTo({
        center: resetCenter,
        zoom: resetZoom,
        duration: FLY_TO_DURATION,
      });
    },
    [mapRef],
  );

  const getCenter = useCallback((): [number, number] | null => {
    const map = mapRef.current?.getMap();
    if (!map) return null;
    const center = map.getCenter();
    return [center.lng, center.lat];
  }, [mapRef]);

  const getZoom = useCallback((): number | null => {
    const map = mapRef.current?.getMap();
    if (!map) return null;
    return map.getZoom();
  }, [mapRef]);

  const fitBounds = useCallback(
    (bounds: LngLatBoundsLike, options?: FitBoundsOptions) => {
      const map = mapRef.current?.getMap();
      if (!map) return;

      const fitBoundsOptions: Parameters<typeof map.fitBounds>[1] = {
        duration: options?.duration ?? FLY_TO_DURATION,
        padding: options?.padding ?? 50,
      };

      if (options?.maxZoom !== undefined) {
        fitBoundsOptions.maxZoom = options.maxZoom;
      }

      map.fitBounds(bounds, fitBoundsOptions);
    },
    [mapRef],
  );

  const fitFeatures = useCallback(
    (
      features: GeoJSON.Feature[] | GeoJSON.FeatureCollection,
      options?: FitFeaturesOptions,
    ) => {
      const map = mapRef.current?.getMap();
      if (!map) return;

      const bounds = calculateBoundsFromFeatures(features);
      if (!bounds) {
        console.warn("Could not calculate bounds from features");
        return;
      }

      map.fitBounds(bounds, {
        padding: options?.padding ?? 50,
        duration: options?.duration ?? FLY_TO_DURATION,
        maxZoom: options?.maxZoom,
      });
    },
    [mapRef],
  );

  const zoomPercentage = zoom !== null && initialZoom !== null && initialZoom > 0
    ? Math.round((zoom / initialZoom) * 100)
    : null;

  return {
    zoom,
    zoomPercentage,
    zoomIn,
    zoomOut,
    setZoom,
    panUp,
    panDown,
    panLeft,
    panRight,
    panTo,
    flyTo,
    reset,
    getCenter,
    getZoom,
    fitBounds,
    fitFeatures,
  };
}

